import { format } from "date-fns";
import * as React from "react";

import {
    AttachmentLink,
    Page,
    PageActionButton,
    PageHeaderActions,
    Paginator,
    Text,
    textWithLinks,
    ScrollContainer,
} from "../../../../";
import {
    EnvelopeIcon,
    EnvelopeOpenIcon,
    ForwardIcon,
    ReplyIcon,
} from "../../../../assets/icons";
import { useMessagingStyles } from "../../styles";
import { MessageDetailsProps } from "./messageDetailsProps";

export const MessageDetailDesktop = (props: MessageDetailsProps) => {
    const {
        label,
        backUrl,
        handleStatus,
        handleComposerOpen,
        handleNavigate,
        title,
        currentMessage,
        loadingMessage,
        messageIds,
        currentIndex,
    } = props;
    const classes = useMessagingStyles({});
    const recipientsDiff =
        currentMessage?.totalRecipientsCount -
        currentMessage?.recipients?.length;

    const renderLeft = () => {
        if (!currentMessage) return null;

        return (
            <>
                {currentMessage.canReply ? (
                    <PageActionButton
                        data-cy="replyMessageIcon"
                        id="replyMessage"
                        onClick={() => handleComposerOpen("reply")}
                        color="primary"
                        title="reply"
                    >
                        <ReplyIcon />
                    </PageActionButton>
                ) : null}
                <PageActionButton
                    data-cy="forwardMessageIcon"
                    id="forwardMessage"
                    onClick={() => handleComposerOpen("forward")}
                    color="primary"
                    title="forward"
                >
                    <ForwardIcon />
                </PageActionButton>
                {currentMessage.canChangeMessageStatus ? (
                    <>
                        {currentMessage.isRead ? (
                            <PageActionButton
                                data-cy="unreadMessageIcon"
                                id="markMessageUnread"
                                onClick={() => handleStatus("unread")}
                                color="primary"
                                title="mark as unread"
                            >
                                <EnvelopeIcon />
                            </PageActionButton>
                        ) : (
                            <PageActionButton
                                data-cy="readMessageIcon"
                                id="markMessageRead"
                                onClick={() => handleStatus("read")}
                                color="primary"
                                title="mark as read"
                            >
                                <EnvelopeOpenIcon />
                            </PageActionButton>
                        )}
                    </>
                ) : null}
            </>
        );
    };

    const renderRight = () => {
        if (!!messageIds && messageIds.length > 0) {
            return (
                <Paginator
                    baseId="messageDetails"
                    isBusy={loadingMessage}
                    current={currentIndex + 1}
                    total={messageIds.length}
                    onNavigate={handleNavigate}
                />
            );
        }
        return null;
    };

    const renderActions = () => (
        <PageHeaderActions left={renderLeft()} right={renderRight()} />
    );

    return (
        <Page
            id="messageDetails"
            label={label}
            title={title}
            isBusy={loadingMessage}
            backUrl={backUrl}
            actions={renderActions()}
            showHeaderShadow={true}
        >
            {!!currentMessage ? (
                <div className={classes.details}>
                    <div>
                        <div className={classes.columnsWrapper}>
                            <div className={classes.left}>
                                <Text
                                    dataCy="messageSenderName"
                                    id="messageDetailsSender"
                                    variant="h2"
                                >
                                    {currentMessage.sender.displayName}
                                </Text>
                            </div>
                            <div className={classes.right}>
                                <Text
                                    dataCy="messageSentDate"
                                    id="messageDetailsDateSent"
                                    variant="body2"
                                >
                                    {format(
                                        new Date(
                                            currentMessage.createTimestamp
                                        ),
                                        "P p"
                                    )}
                                </Text>
                            </div>
                        </div>
                        <div className={classes.wrapper}>
                            <div className={classes.toWrapper}>
                                <Text
                                    variant="body1"
                                    isInline
                                    className={classes.detailsTo}
                                >
                                    to:
                                </Text>
                                <Text dataCy="messageRecipients" variant="h5">
                                    {currentMessage.recipients.map(
                                        (recipient, i) => (
                                            <span key={i}>
                                                {recipient.displayName}
                                                {currentMessage.recipients
                                                    .length -
                                                    1 >
                                                i
                                                    ? ", "
                                                    : null}
                                            </span>
                                        )
                                    )}
                                    {recipientsDiff > 0
                                        ? ` and ${recipientsDiff} others`
                                        : null}
                                </Text>
                            </div>
                        </div>
                        {currentMessage.attachments &&
                        currentMessage.attachments.length > 0 ? (
                            <div className={classes.attachmentsWrapper}>
                                {currentMessage.attachments.map((att, i) => (
                                    <AttachmentLink
                                        baseId="messagesDetails"
                                        classes={{
                                            root: classes.attachments,
                                        }}
                                        key={i}
                                        icon={true}
                                        url={att.fileUri}
                                        playMediaInNewWindow={true}
                                        label={att.fileName}
                                        type={att.contentType}
                                        highlightType="internal"
                                    />
                                ))}
                            </div>
                        ) : null}
                    </div>
                    <ScrollContainer>
                        <div
                            data-cy="messageBody"
                            className={classes.messageBody}
                        >
                            {currentMessage.isHtml === true ? (
                                <iframe
                                    key={currentMessage.messageId}
                                    title={currentMessage.messageId.toString()}
                                    srcDoc={currentMessage.body}
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        border: "none",
                                        margin: 0,
                                    }}
                                />
                            ) : (
                                textWithLinks(currentMessage.body)
                            )}
                        </div>
                    </ScrollContainer>
                </div>
            ) : null}
        </Page>
    );
};
